import { db } from "@db";
import { createNucleotideSequence } from "@db/createNucleotideSequence";
import { groupAsCollection } from "@db/groupAsCollection";
import { fetchGenbankBatch, type NcbiCredentials } from "@fetch/genbank/fetch";
import { transformGenbankFeature } from "@formats/genbank/transform";
import { useCurrentUser } from "@hooks/use-current-user";
import { useStorageQuota } from "@hooks/use-storage-quota";
import { getErrorMessage } from "@lib/errors";
import { extractSequenceMetadata } from "@upload/ingest/genbank";
import { useEffect, useRef, useState } from "react";
import { parseAccessions } from "./parse";
import type { AccessionItem } from "./types";

type UseAccessionImportOptions = {
  input: string;
  collectionName: string;
  asCollection: boolean;
  onClose: () => void;
};

export function useAccessionImport({
  input,
  collectionName,
  asCollection,
  onClose,
}: UseAccessionImportOptions) {
  const [results, setResults] = useState<Map<string, AccessionItem>>(new Map());
  const [fetching, setFetching] = useState(false);
  const { user } = useCurrentUser();
  const { isOverQuota } = useStorageQuota();
  const ownerId = user?.id ?? "local";
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const accessions = parseAccessions(input);

  const visibleItems = accessions.flatMap((a) => {
    const item = results.get(a);
    return item ? [item] : [];
  });

  const unfetchedAccessions = accessions.filter((a) => !results.has(a));
  const erroredAccessions = accessions.filter(
    (a) => results.get(a)?.status === "error",
  );

  const successCount = visibleItems.filter(
    (item) => item.status === "complete",
  ).length;

  const canFetch =
    (unfetchedAccessions.length > 0 || erroredAccessions.length > 0) &&
    !fetching;
  const canCreate = successCount > 0 && !fetching && !isOverQuota;
  const hasMultiple = successCount > 1;

  const firstSuccessfulRecord = visibleItems.find(
    (item) => item.status === "complete" && item.record,
  )?.record;
  const defaultCollectionName = firstSuccessfulRecord
    ? firstSuccessfulRecord.definition || firstSuccessfulRecord.locus.name
    : "";

  async function handleFetch(accessionsToFetch?: string[]) {
    const toFetch = accessionsToFetch ?? [
      ...unfetchedAccessions,
      ...erroredAccessions,
    ];

    if (toFetch.length === 0) return;

    setFetching(true);

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setResults((prev) => {
      const next = new Map(prev);
      for (const accession of toFetch) {
        next.set(accession, { accession, status: "fetching" });
      }
      return next;
    });

    try {
      const settings = await db.settings.get("settings");

      let credentials: NcbiCredentials | undefined;

      if (settings?.ncbiApiKey) {
        credentials = {
          apiKey: settings.ncbiApiKey,
          email: settings.ncbiUsername,
        };
      }

      const result = await fetchGenbankBatch(
        toFetch,
        credentials,
        controller.signal,
      );

      setResults((prev) => {
        const next = new Map(prev);
        for (const accession of toFetch) {
          const record = result.records.get(accession);
          const error = result.errors.get(accession);

          if (record) {
            next.set(accession, { accession, status: "complete", record });
          } else {
            next.set(accession, {
              accession,
              status: "error",
              error: error ?? "Unknown error",
            });
          }
        }
        return next;
      });
    } catch (err) {
      setResults((prev) => {
        const next = new Map(prev);
        for (const accession of toFetch) {
          next.set(accession, {
            accession,
            status: "error",
            error: getErrorMessage(err, "Fetch failed"),
          });
        }
        return next;
      });
    } finally {
      setFetching(false);
    }
  }

  async function handleSubmit() {
    if (!canCreate) return;

    const successfulRecords = visibleItems.flatMap((item) =>
      item.status === "complete" && item.record ? [item.record] : [],
    );

    const results = await Promise.all(
      successfulRecords.map((record) => {
        const name = record.definition || record.locus.name;
        const topology =
          record.locus.topology === "circular"
            ? ("circular" as const)
            : ("linear" as const);
        const features = record.features.map((genbankFeature) => {
          const { sequenceId: _, ...feature } = transformGenbankFeature(
            genbankFeature,
            "",
          );
          return feature;
        });

        return createNucleotideSequence({
          name,
          sequence: record.sequence,
          ownerId,
          topology,
          ...extractSequenceMetadata(record),
          features,
        });
      }),
    );

    if (asCollection && hasMultiple) {
      const name = collectionName.trim() || defaultCollectionName;
      await groupAsCollection(results, name, ownerId);
    }

    onClose();
  }

  return {
    fetching,
    isOverQuota,
    accessions,
    visibleItems,
    unfetchedAccessions,
    successCount,
    canFetch,
    canCreate,
    hasMultiple,
    defaultCollectionName,
    handleFetch,
    handleSubmit,
  };
}
